import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../redux/hook';
import {
  savePageState,
  clearPageState,
  selectPageState,
  PageState,
} from '../redux/slices/pageStates.slice';

/**
 * Hook personalizado para manejar el estado de páginas específicas
 * @param customPath Ruta personalizada en lugar de usar la ruta actual (opcional)
 * @returns Objeto con funciones para manipular el estado de la página
 */
export function usePageState<T extends PageState>(customPath?: string) {
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Usar la ruta personalizada o la ruta actual
  const path = customPath || location.pathname;

  // Obtener el estado actual de la página
  const state = useAppSelector(selectPageState(path)) as T;

  // Guardar el estado completo o parcial de la página
  const saveState = useCallback(
    (newState: Partial<T>) => {
      dispatch(
        savePageState({
          path,
          state: newState as PageState,
        }),
      );
    },
    [dispatch, path],
  );

  // Limpiar el estado de la página
  const clearState = useCallback(() => {
    dispatch(clearPageState({ path }));
  }, [dispatch, path]);

  return {
    state,
    saveState,
    clearState,
  };
}
